/**
 * Host Global - Check-In/Check-Out Agent Dashboard
 * View and manage assigned reservations for check-in and check-out
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import apiRequest from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function CheckInOutDashboard() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0); // 0 = Check-In, 1 = Check-Out
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // "checkin" or "checkout"
  const [submitting, setSubmitting] = useState(false);
  const [actionData, setActionData] = useState({
    notes: "",
    photos: [],
    damagedItems: [], // Array of { name: "", description: "" } - cost estimate in 2-4 days
    guests: [], // Array of { name: "", idFrontPhoto: null, idBackPhoto: null }
    criteriaConfirmed: [], // Array of { criterionName, criterionNameAr, category, present }
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all"); // all, today, overdue, upcoming
  const [sortBy, setSortBy] = useState("date"); // date, property, client
  const [sortOrder, setSortOrder] = useState("asc"); // asc, desc
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Fetch reservations
  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      const response = await apiRequest("/reservations");
      setReservations(response.reservations || response.data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load reservations. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort reservations for Check-In tab
  const checkInReservations = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let filtered = reservations.filter((reservation) => {
      // Show reservations that are Pending or Confirmed (not yet checked in)
      if (reservation.status !== "Confirmed" && reservation.status !== "Pending") {
        return false;
      }

      // Must have a check-in date
      if (!reservation.checkInDate) {
        return false;
      }

      const checkInDate = new Date(reservation.checkInDate);
      checkInDate.setHours(0, 0, 0, 0);

      // Show all pending/confirmed reservations regardless of date (they need check-in)
      // But prioritize today and future dates
      return true;
    });

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((reservation) => {
        const reservationNumber = (reservation.reservationNumber || "").toLowerCase();
        const propertyName = (
          reservation.property?.title?.en ||
          reservation.property?.title ||
          ""
        ).toLowerCase();
        const clientName = (
          reservation.client?.fullName ||
          reservation.client?.email ||
          ""
        ).toLowerCase();
        return (
          reservationNumber.includes(query) ||
          propertyName.includes(query) ||
          clientName.includes(query)
        );
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((reservation) => reservation.status === statusFilter);
    }

    // Apply date filter
    if (dateFilter !== "all") {
      filtered = filtered.filter((reservation) => {
        const checkInDate = new Date(reservation.checkInDate);
        checkInDate.setHours(0, 0, 0, 0);
        if (dateFilter === "today") {
          return checkInDate.getTime() === today.getTime();
        } else if (dateFilter === "overdue") {
          return checkInDate < today;
        } else if (dateFilter === "upcoming") {
          return checkInDate > today;
        }
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      if (sortBy === "date") {
        aValue = new Date(a.checkInDate);
        bValue = new Date(b.checkInDate);
        // Prioritize today, then future, then past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        aValue.setHours(0, 0, 0, 0);
        bValue.setHours(0, 0, 0, 0);

        const aIsToday = aValue.getTime() === today.getTime();
        const bIsToday = bValue.getTime() === today.getTime();
        const aIsFuture = aValue > today;
        const bIsFuture = bValue > today;

        // Today first
        if (aIsToday && !bIsToday) return -1;
        if (!aIsToday && bIsToday) return 1;
        // Then future dates
        if (aIsFuture && !bIsFuture && !bIsToday) return -1;
        if (!aIsFuture && bIsFuture && !aIsToday) return 1;
        // Then sort by date
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      } else if (sortBy === "property") {
        aValue = (a.property?.title?.en || a.property?.title || "").toLowerCase();
        bValue = (b.property?.title?.en || b.property?.title || "").toLowerCase();
      } else if (sortBy === "client") {
        aValue = (a.client?.fullName || a.client?.email || "").toLowerCase();
        bValue = (b.client?.fullName || b.client?.email || "").toLowerCase();
      }

      if (sortBy !== "date") {
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      }
    });

    return filtered;
  }, [reservations, searchQuery, statusFilter, dateFilter, sortBy, sortOrder]);

  // Filter and sort reservations for Check-Out tab
  const checkOutReservations = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let filtered = reservations.filter((reservation) => {
      // Only show reservations that have already checked in (do not show Confirmed/pending)
      if (reservation.status !== "CheckedIn") {
        return false;
      }

      if (!reservation.checkOutDate) {
        return false;
      }

      return true;
    });

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((reservation) => {
        const reservationNumber = (reservation.reservationNumber || "").toLowerCase();
        const propertyName = (
          reservation.property?.title?.en ||
          reservation.property?.title ||
          ""
        ).toLowerCase();
        const clientName = (
          reservation.client?.fullName ||
          reservation.client?.email ||
          ""
        ).toLowerCase();
        return (
          reservationNumber.includes(query) ||
          propertyName.includes(query) ||
          clientName.includes(query)
        );
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((reservation) => reservation.status === statusFilter);
    }

    // Apply date filter
    if (dateFilter !== "all") {
      filtered = filtered.filter((reservation) => {
        const checkOutDate = new Date(reservation.checkOutDate);
        checkOutDate.setHours(0, 0, 0, 0);
        if (dateFilter === "today") {
          return checkOutDate.getTime() === today.getTime();
        } else if (dateFilter === "overdue") {
          return checkOutDate < today;
        } else if (dateFilter === "upcoming") {
          return checkOutDate > today;
        }
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      if (sortBy === "date") {
        aValue = new Date(a.checkOutDate);
        bValue = new Date(b.checkOutDate);
      } else if (sortBy === "property") {
        aValue = (a.property?.title?.en || a.property?.title || "").toLowerCase();
        bValue = (b.property?.title?.en || b.property?.title || "").toLowerCase();
      } else if (sortBy === "client") {
        aValue = (a.client?.fullName || a.client?.email || "").toLowerCase();
        bValue = (b.client?.fullName || b.client?.email || "").toLowerCase();
      }

      if (sortBy === "date") {
        // For checkout, prioritize overdue first, then today, then future
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        aValue.setHours(0, 0, 0, 0);
        bValue.setHours(0, 0, 0, 0);

        if (aValue < today && bValue >= today) return -1;
        if (aValue >= today && bValue < today) return 1;
        if (aValue.getTime() === today.getTime() && bValue > today) return -1;
        if (aValue > today && bValue.getTime() === today.getTime()) return 1;

        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      } else {
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      }
    });

    return filtered;
  }, [reservations, searchQuery, statusFilter, dateFilter, sortBy, sortOrder]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Format date - ensure it's on a single line
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check-in time: default 2:00 PM, or requested time when set
  const getCheckinTimeDisplay = (reservation) => {
    if (reservation?.requestedCheckInTime) {
      return new Date(reservation.requestedCheckInTime).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    return "2:00 PM";
  };

  // Checkout time: default 12:00 PM, early checkout same-day time, or late checkout time
  const getCheckoutTimeDisplay = (reservation) => {
    if (
      reservation?.earlyCheckout?.type === "sameDay" &&
      reservation?.earlyCheckout?.status === "completed" &&
      reservation?.earlyCheckout?.requestedCheckOutTime
    ) {
      return new Date(reservation.earlyCheckout.requestedCheckOutTime).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    if (reservation?.lateCheckOutHours > 0 && reservation?.requestedCheckOutTime) {
      return new Date(reservation.requestedCheckOutTime).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    return "12:00 PM";
  };

  // Handle check-in
  const handleCheckIn = (reservation) => {
    setSelectedReservation(reservation);
    setActionType("checkin");
    const numberOfGuests = reservation.numberOfGuests || 1;
    const initialGuests = Array.from({ length: numberOfGuests }, () => ({
      name: "",
      idFrontPhoto: null,
      idBackPhoto: null,
    }));
    const criteria = reservation?.property?.criteria || [];
    const criteriaConfirmed = criteria.map((c) => ({
      criterionName: c.name?.en || "",
      criterionNameAr: c.name?.ar || "",
      category: c.category?.en || c.category || "",
      quantifiable: c.quantifiable === true,
      quantity:
        c.quantifiable && c.quantity != null
          ? Math.max(1, parseInt(c.quantity, 10) || 1)
          : undefined,
      present: true,
    }));
    setActionData({
      notes: "",
      photos: [],
      damagedItems: [],
      guests: initialGuests,
      criteriaConfirmed,
    });
    setDialogOpen(true);
  };

  // Handle check-out
  const handleCheckOut = (reservation) => {
    setSelectedReservation(reservation);
    setActionType("checkout");
    const criteria =
      reservation?.checkIn?.criteriaConfirmed?.length > 0
        ? reservation.checkIn.criteriaConfirmed.map((c) => ({ ...c, present: c.present !== false }))
        : (reservation?.property?.criteria || []).map((c) => ({
            criterionName: c.name?.en || "",
            criterionNameAr: c.name?.ar || "",
            category: c.category?.en || c.category || "",
            quantifiable: c.quantifiable === true,
            quantity: c.quantifiable && c.quantity != null ? c.quantity : undefined,
            present: true,
          }));
    setActionData({
      notes: "",
      photos: [],
      damagedItems: [],
      criteriaConfirmed: criteria,
    });
    setDialogOpen(true);
  };

  // Damaged items input
  const [damagedItemName, setDamagedItemName] = useState("");
  const [damagedItemDesc, setDamagedItemDesc] = useState("");
  const handleAddDamagedItem = () => {
    const name = damagedItemName?.trim();
    if (!name) return;
    setActionData((prev) => ({
      ...prev,
      damagedItems: [
        ...(prev.damagedItems || []),
        { name, description: damagedItemDesc?.trim() || "" },
      ],
    }));
    setDamagedItemName("");
    setDamagedItemDesc("");
  };
  const handleRemoveDamagedItem = (idx) => {
    setActionData((prev) => ({
      ...prev,
      damagedItems: prev.damagedItems.filter((_, i) => i !== idx),
    }));
  };

  // Submit check-in/check-out
  const handleSubmitAction = async () => {
    if (!selectedReservation) return;

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const endpoint =
        actionType === "checkin"
          ? `/reservations/${selectedReservation._id || selectedReservation.id}/check-in`
          : `/reservations/${selectedReservation._id || selectedReservation.id}/check-out`;

      // For check-in, validate guest information
      if (actionType === "checkin") {
        const missingFields = actionData.guests?.some((guest, index) => {
          if (!guest.name || !guest.name.trim()) {
            setMessage({
              type: "error",
              text: `Please enter name for Guest ${index + 1}`,
            });
            setSubmitting(false);
            return true;
          }
          return false;
        });

        if (missingFields) {
          return;
        }
      }

      // Create FormData for check-in (has file uploads) or JSON for check-out
      if (actionType === "checkin" && actionData.guests?.length > 0) {
        const formData = new FormData();

        formData.append("notes", actionData.notes || "");
        if (actionData.criteriaConfirmed?.length > 0) {
          formData.append("criteriaConfirmed", JSON.stringify(actionData.criteriaConfirmed));
        }

        const guestsToSend = actionData.guests
          .filter((g) => g && g.name && g.name.trim())
          .map((g) => ({
            name: g.name.trim(),
            idFrontPhoto: null,
            idBackPhoto: null,
          }));
        formData.append("guestsJson", JSON.stringify(guestsToSend));

        actionData.guests.forEach((guest, index) => {
          if (guest.name && guest.name.trim()) {
            formData.append(`guests[${index}][name]`, guest.name.trim());
            if (guest.idFrontPhoto && guest.idFrontPhoto instanceof File) {
              formData.append(`guests[${index}][idFrontPhoto]`, guest.idFrontPhoto);
            }
            if (guest.idBackPhoto && guest.idBackPhoto instanceof File) {
              formData.append(`guests[${index}][idBackPhoto]`, guest.idBackPhoto);
            }
          }
        });

        const response = await apiRequest(endpoint, {
          method: "PATCH",
          body: formData,
        });

        setMessage({
          type: "success",
          text: "Check-in completed successfully!",
        });

        setDialogOpen(false);
        setSelectedReservation(null);
        setActionType(null);
        setActionData({
          notes: "",
          photos: [],
          damagedItems: [],
          guests: [],
          criteriaConfirmed: [],
        });

        // Refresh reservations
        await fetchReservations();

        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        setSubmitting(false);
        return;
      }

      // For check-out or check-in without files, use JSON
      const payload = {
        notes: actionData.notes,
        photos: actionData.photos,
      };

      if (actionType === "checkout") {
        payload.damagedItems = actionData.damagedItems || [];
        if (actionData.criteriaConfirmed?.length > 0) {
          payload.criteriaConfirmed = actionData.criteriaConfirmed;
        }
      }

      const response = await apiRequest(endpoint, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      setMessage({
        type: "success",
        text:
          actionType === "checkin"
            ? "Check-in completed successfully!"
            : "Check-out completed successfully!",
      });

      setDialogOpen(false);
      setSelectedReservation(null);
      setActionType(null);
      setActionData({ notes: "", photos: [], damagedItems: [], guests: [], criteriaConfirmed: [] });

      // Refresh reservations
      await fetchReservations();

      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error(`Error performing ${actionType}:`, error);
      setMessage({
        type: "error",
        text:
          error.message ||
          `Failed to complete ${actionType === "checkin" ? "check-in" : "check-out"}`,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const currentReservations = tabValue === 0 ? checkInReservations : checkOutReservations;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox mb={4}>
          <MDTypography variant="h4" fontWeight="bold" mb={1}>
            Check-In / Check-Out Dashboard
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Manage check-in and check-out processes for assigned reservations
          </MDTypography>
        </MDBox>

        {/* Message Alert - success only; errors show inside the modal */}
        {message.text && !(dialogOpen && message.type === "error") && (
          <Alert
            severity={message.type === "error" ? "error" : "success"}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        {/* Tabs */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => {
              setTabValue(newValue);
              // Reset filters when switching tabs
              setSearchQuery("");
              setStatusFilter("all");
              setDateFilter("all");
            }}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: 64,
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
              },
            }}
          >
            <Tab
              label={
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon>login</Icon>
                  Check-In
                  {checkInReservations.length > 0 && (
                    <Chip
                      label={checkInReservations.length}
                      size="small"
                      color="info"
                      sx={{ ml: 1, fontWeight: 700 }}
                    />
                  )}
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const todayCount = checkInReservations.filter((r) => {
                      if (!r.checkInDate) return false;
                      const checkInDate = new Date(r.checkInDate);
                      checkInDate.setHours(0, 0, 0, 0);
                      return checkInDate.getTime() === today.getTime();
                    }).length;
                    return todayCount > 0 ? (
                      <Chip
                        label={`${todayCount} Today`}
                        size="small"
                        color="warning"
                        sx={{ ml: 1, fontWeight: 700 }}
                      />
                    ) : null;
                  })()}
                </MDBox>
              }
            />
            <Tab
              label={
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon>logout</Icon>
                  Check-Out
                  {checkOutReservations.length > 0 && (
                    <Chip
                      label={checkOutReservations.length}
                      size="small"
                      color="warning"
                      sx={{ ml: 1, fontWeight: 700 }}
                    />
                  )}
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const todayCount = checkOutReservations.filter((r) => {
                      if (!r.checkOutDate) return false;
                      const checkOutDate = new Date(r.checkOutDate);
                      checkOutDate.setHours(0, 0, 0, 0);
                      return checkOutDate.getTime() === today.getTime();
                    }).length;
                    return todayCount > 0 ? (
                      <Chip
                        label={`${todayCount} Today`}
                        size="small"
                        color="warning"
                        sx={{ ml: 1, fontWeight: 700 }}
                      />
                    ) : null;
                  })()}
                </MDBox>
              }
            />
          </Tabs>
        </Card>

        {/* Filters Section */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <MDBox p={2}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="bold">
                Filters & Search
              </MDTypography>
              <MDButton
                variant="text"
                color="info"
                size="small"
                onClick={() => setFiltersOpen(!filtersOpen)}
                startIcon={<Icon>{filtersOpen ? "expand_less" : "expand_more"}</Icon>}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}
              </MDButton>
            </MDBox>

            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder={`Search by reservation number, property, or client...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>search</Icon>
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <Icon fontSize="small">clear</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Advanced Filters */}
            <Collapse in={filtersOpen}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Confirmed">Confirmed</MenuItem>
                      <MenuItem value="CheckedIn">Checked In</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Date Filter</InputLabel>
                    <Select
                      value={dateFilter}
                      label="Date Filter"
                      onChange={(e) => setDateFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Dates</MenuItem>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                      <MenuItem value="upcoming">Upcoming</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      label="Sort By"
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="date">Date</MenuItem>
                      <MenuItem value="property">Property</MenuItem>
                      <MenuItem value="client">Client</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Order</InputLabel>
                    <Select
                      value={sortOrder}
                      label="Order"
                      onChange={(e) => setSortOrder(e.target.value)}
                    >
                      <MenuItem value="asc">Ascending</MenuItem>
                      <MenuItem value="desc">Descending</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("all");
                      setDateFilter("all");
                      setSortBy("date");
                      setSortOrder("asc");
                    }}
                    startIcon={<Icon>refresh</Icon>}
                  >
                    Reset Filters
                  </MDButton>
                </Grid>
              </Grid>
            </Collapse>
          </MDBox>
        </Card>

        {/* Reservations Table */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <MDBox p={3}>
            {/* Results Count */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
                  {tabValue === 0 ? "Check-In" : "Check-Out"} Reservations
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Showing {currentReservations.length}{" "}
                  {currentReservations.length === 1 ? "reservation" : "reservations"}
                </MDTypography>
              </MDBox>
              {(searchQuery || statusFilter !== "all" || dateFilter !== "all") && (
                <Chip
                  label="Filters Active"
                  color="info"
                  size="small"
                  icon={<Icon>filter_list</Icon>}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </MDBox>

            {currentReservations.length === 0 ? (
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={8}
              >
                <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>
                  {tabValue === 0 ? "event_available" : "event_busy"}
                </Icon>
                <MDTypography variant="h6" color="text.secondary" mb={1}>
                  No {tabValue === 0 ? "Check-In" : "Check-Out"} Reservations
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  {tabValue === 0
                    ? "No reservations require check-in at this time."
                    : "No reservations require check-out at this time."}
                </MDTypography>
              </MDBox>
            ) : (
              <TableContainer
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "auto",
                }}
              >
                <Table
                  sx={{
                    width: "100%",
                    tableLayout: "fixed",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: "grey.100",
                        "& .MuiTableCell-head": {
                          borderBottom: "2px solid",
                          borderColor: "divider",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          py: 2,
                          px: 3,
                        },
                      }}
                    >
                      <TableCell sx={{ width: "11%", minWidth: "110px" }} align="left">
                        Reservation
                      </TableCell>
                      <TableCell sx={{ width: "15%", minWidth: "150px" }} align="left">
                        Property
                      </TableCell>
                      <TableCell sx={{ width: "16%", minWidth: "160px" }} align="left">
                        Client
                      </TableCell>
                      <TableCell sx={{ width: "11%", minWidth: "110px" }} align="left">
                        {tabValue === 0 ? "Check-In Date" : "Check-Out Date"}
                      </TableCell>
                      <TableCell sx={{ width: "13%", minWidth: "130px" }} align="left">
                        Check-in time
                      </TableCell>
                      <TableCell sx={{ width: "13%", minWidth: "130px" }} align="left">
                        Check-out time
                      </TableCell>
                      <TableCell sx={{ width: "5%", minWidth: "50px" }} align="center">
                        Guests
                      </TableCell>
                      <TableCell sx={{ width: "7%", minWidth: "100px" }} align="right">
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {currentReservations.map((reservation) => {
                      // Calculate if date is today
                      const isToday = (() => {
                        const date =
                          tabValue === 0 ? reservation.checkInDate : reservation.checkOutDate;
                        if (!date) return false;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const checkDate = new Date(date);
                        checkDate.setHours(0, 0, 0, 0);
                        return checkDate.getTime() === today.getTime();
                      })();

                      // Calculate if checkout is overdue (for checkout tab only)
                      const isOverdue = (() => {
                        if (tabValue !== 1 || !reservation.checkOutDate) return false;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const checkOutDate = new Date(reservation.checkOutDate);
                        checkOutDate.setHours(0, 0, 0, 0);
                        return checkOutDate < today;
                      })();

                      // Get the date to display (check-in or checkout)
                      const displayDate =
                        tabValue === 0 ? reservation.checkInDate : reservation.checkOutDate;

                      return (
                        <TableRow
                          key={reservation._id || reservation.id}
                          hover
                          onClick={() =>
                            navigate(
                              `/checkin-out/reservation/${reservation._id || reservation.id}`
                            )
                          }
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "grey.50" },
                            "& .MuiTableCell-root": {
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              py: 2,
                              px: 3,
                              verticalAlign: "middle",
                            },
                            "&:last-child .MuiTableCell-root": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell
                            align="left"
                            sx={{
                              width: "11%",
                              minWidth: "110px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 0,
                            }}
                          >
                            <MDTypography
                              variant="body2"
                              fontWeight="medium"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={reservation.reservationNumber || "N/A"}
                            >
                              {reservation.reservationNumber || "N/A"}
                            </MDTypography>
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{
                              width: "15%",
                              minWidth: "150px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 0,
                            }}
                          >
                            <MDTypography
                              variant="body2"
                              fontWeight="medium"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={
                                reservation.property?.title?.en ||
                                reservation.property?.title ||
                                "N/A"
                              }
                            >
                              {reservation.property?.title?.en ||
                                reservation.property?.title ||
                                "N/A"}
                            </MDTypography>
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{
                              width: "16%",
                              minWidth: "160px",
                              overflow: "hidden",
                              maxWidth: 0,
                            }}
                          >
                            <MDBox>
                              <MDTypography
                                variant="body2"
                                fontWeight="medium"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "block",
                                }}
                                title={
                                  reservation.client?.fullName || reservation.client?.email || "N/A"
                                }
                              >
                                {reservation.client?.fullName || reservation.client?.email || "N/A"}
                              </MDTypography>
                              {reservation.client?.phone && (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.5,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                  }}
                                  title={reservation.client.phone}
                                >
                                  {reservation.client.phone}
                                </MDTypography>
                              )}
                            </MDBox>
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{
                              width: "11%",
                              minWidth: "110px",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 0,
                            }}
                          >
                            <MDBox>
                              <MDTypography
                                variant="body2"
                                fontWeight="medium"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "block",
                                }}
                              >
                                {displayDate ? formatDate(displayDate) : "N/A"}
                              </MDTypography>
                              {(isToday || isOverdue || reservation.extendedStayRequestedAt) && (
                                <MDBox display="flex" gap={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                  {isToday && (
                                    <Chip
                                      label="Today"
                                      size="small"
                                      color="warning"
                                      sx={{ fontWeight: 500, fontSize: "0.65rem", height: "20px" }}
                                    />
                                  )}
                                  {isOverdue && (
                                    <Chip
                                      label="Overdue"
                                      size="small"
                                      color="error"
                                      sx={{ fontWeight: 500, fontSize: "0.65rem", height: "20px" }}
                                    />
                                  )}
                                  {reservation.extendedStayRequestedAt && (
                                    <Chip
                                      label="Extended stay"
                                      size="small"
                                      color="info"
                                      sx={{ fontWeight: 500, fontSize: "0.65rem", height: "20px" }}
                                      title="Client has added additional days"
                                    />
                                  )}
                                </MDBox>
                              )}
                            </MDBox>
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{
                              width: "13%",
                              minWidth: "130px",
                              overflow: "hidden",
                              verticalAlign: "top",
                            }}
                          >
                            <MDBox>
                              <MDTypography variant="body2" fontWeight="medium">
                                {getCheckinTimeDisplay(reservation)}
                              </MDTypography>
                              <MDBox display="flex" gap={0.5} sx={{ mt: 0.75, flexWrap: "wrap" }}>
                                {reservation.requestedCheckInTime &&
                                  (() => {
                                    const requestedTime = new Date(
                                      reservation.requestedCheckInTime
                                    );
                                    const hours = requestedTime.getUTCHours();
                                    return hours < 14;
                                  })() && (
                                    <Chip
                                      icon={
                                        <Icon sx={{ fontSize: "0.875rem !important" }}>
                                          schedule
                                        </Icon>
                                      }
                                      label="Early check-in"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        height: "22px",
                                        borderWidth: 1.5,
                                      }}
                                      title="Guest requested check-in before standard 2:00 PM"
                                    />
                                  )}
                              </MDBox>
                            </MDBox>
                          </TableCell>

                          <TableCell
                            align="left"
                            sx={{
                              width: "13%",
                              minWidth: "130px",
                              overflow: "hidden",
                              verticalAlign: "top",
                            }}
                          >
                            <MDBox>
                              <MDTypography variant="body2" fontWeight="medium">
                                {getCheckoutTimeDisplay(reservation)}
                              </MDTypography>
                              <MDBox display="flex" gap={0.5} sx={{ mt: 0.75, flexWrap: "wrap" }}>
                                {reservation.earlyCheckout?.type === "sameDay" &&
                                  reservation.earlyCheckout?.status === "completed" && (
                                    <Chip
                                      icon={
                                        <Icon sx={{ fontSize: "0.875rem !important" }}>
                                          schedule
                                        </Icon>
                                      }
                                      label="Early checkout"
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        height: "22px",
                                        borderWidth: 1.5,
                                      }}
                                      title="Guest chose earlier checkout time on same day (no fee)"
                                    />
                                  )}
                                {reservation.earlyCheckout?.type === "byDays" &&
                                  reservation.earlyCheckout?.status === "completed" && (
                                    <Chip
                                      icon={
                                        <Icon sx={{ fontSize: "0.875rem !important" }}>
                                          event_available
                                        </Icon>
                                      }
                                      label="Early checkout (by days)"
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        height: "22px",
                                        borderWidth: 1.5,
                                      }}
                                      title="Guest checked out on earlier date; fee and refund applied"
                                    />
                                  )}
                                {(reservation.lateCheckOutHours ?? 0) > 0 && (
                                  <Chip
                                    icon={
                                      <Icon sx={{ fontSize: "0.875rem !important" }}>schedule</Icon>
                                    }
                                    label="Late checkout"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: "22px",
                                      borderWidth: 1.5,
                                    }}
                                    title={`Extra ${reservation.lateCheckOutHours} hour(s) after 12:00 PM`}
                                  />
                                )}
                              </MDBox>
                            </MDBox>
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{ width: "5%", minWidth: "50px", overflow: "hidden" }}
                          >
                            <MDBox display="flex" justifyContent="center" alignItems="center">
                              <MDTypography variant="body2" fontWeight="medium">
                                {reservation.numberOfGuests || 0}
                              </MDTypography>
                            </MDBox>
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{ width: "7%", minWidth: "100px" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MDBox
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={1}
                            >
                              {tabValue === 0 ? (
                                <MDButton
                                  variant="gradient"
                                  color="success"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckIn(reservation);
                                  }}
                                  startIcon={<Icon sx={{ fontSize: "1rem" }}>login</Icon>}
                                  sx={{
                                    minHeight: "36px",
                                    minWidth: "110px",
                                    borderRadius: 1.5,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    boxShadow: "none",
                                    "&:hover": {
                                      boxShadow: 2,
                                    },
                                  }}
                                  disabled={
                                    reservation.status !== "Confirmed" &&
                                    reservation.status !== "Pending"
                                    // TEMPORARILY DISABLED: Date validation for testing
                                    // || !isToday
                                  }
                                  title={
                                    // TEMPORARILY DISABLED: Date validation for testing
                                    // !isToday
                                    //   ? `Check-in is only allowed on the check-in date (${displayDate ? formatDate(displayDate) : "N/A"})`
                                    //   :
                                    reservation.status !== "Confirmed" &&
                                    reservation.status !== "Pending"
                                      ? "Reservation must be confirmed or pending"
                                      : ""
                                  }
                                >
                                  Check In
                                </MDButton>
                              ) : (
                                <MDButton
                                  variant="gradient"
                                  color="warning"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckOut(reservation);
                                  }}
                                  startIcon={<Icon sx={{ fontSize: "1rem" }}>logout</Icon>}
                                  sx={{
                                    minHeight: "36px",
                                    minWidth: "110px",
                                    borderRadius: 1.5,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    boxShadow: "none",
                                    "&:hover": {
                                      boxShadow: 2,
                                    },
                                  }}
                                  disabled={reservation.status !== "CheckedIn"}
                                  // TEMPORARILY DISABLED: Date validation for testing
                                  // || !isToday
                                  title={
                                    // TEMPORARILY DISABLED: Date validation for testing
                                    // !isToday
                                    //   ? `Check-out is only allowed on the check-out date (${displayDate ? formatDate(displayDate) : "N/A"})`
                                    //   :
                                    reservation.status !== "CheckedIn"
                                      ? "Reservation must be checked in first"
                                      : ""
                                  }
                                >
                                  Check Out
                                </MDButton>
                              )}
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Check-In/Check-Out Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
              <Icon color={actionType === "checkin" ? "success" : "warning"}>
                {actionType === "checkin" ? "login" : "logout"}
              </Icon>
              {actionType === "checkin" ? "Check-In Reservation" : "Check-Out Reservation"}
            </MDTypography>
            <IconButton onClick={() => !submitting && setDialogOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          {message.type === "error" && message.text && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}
          {selectedReservation && (
            <MDBox>
              {/* Check-in/Check-out Agent (this property) */}
              {(() => {
                const agent =
                  selectedReservation.assignedCheckInOutAgent ||
                  selectedReservation.property?.assignedCheckInOutAgent;
                return agent ? (
                  <MDBox
                    sx={{
                      bgcolor: "info.lighter",
                      p: 2,
                      borderRadius: 2,
                      mb: 3,
                      border: "1px solid",
                      borderColor: "info.light",
                    }}
                  >
                    <MDTypography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={1}
                      color="info.dark"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <Icon sx={{ fontSize: 18 }}>person</Icon>
                      Check-in/Check-out Agent (this property)
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" gap={0.5}>
                      <MDTypography variant="body2">
                        <strong>Name:</strong> {agent.fullName || agent.firstName || "—"}
                      </MDTypography>
                      <MDTypography variant="body2">
                        <strong>Email:</strong> {agent.email || "—"}
                      </MDTypography>
                      <MDTypography variant="body2">
                        <strong>Phone:</strong> {agent.phone || agent.companyNumber || "—"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                ) : null;
              })()}

              {/* Extended stay notice */}
              {selectedReservation.extendedStayRequestedAt && (
                <Alert
                  severity="info"
                  icon={<Icon>event_available</Icon>}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  <MDTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Client has added additional days
                  </MDTypography>
                  <MDTypography variant="body2" component="span">
                    The guest requested to extend their stay.
                    {selectedReservation.originalCheckOutDateBeforeExtension && (
                      <>
                        {" "}
                        Original check-out was{" "}
                        {formatDate(selectedReservation.originalCheckOutDateBeforeExtension)}.{" "}
                      </>
                    )}
                    Current check-out date:{" "}
                    {selectedReservation.checkOutDate
                      ? formatDate(selectedReservation.checkOutDate)
                      : "N/A"}
                    .
                  </MDTypography>
                </Alert>
              )}

              {/* Early checkout notice – same-day or by-days (completed) */}
              {selectedReservation.earlyCheckout?.type === "sameDay" &&
                selectedReservation.earlyCheckout?.status === "completed" && (
                  <Alert
                    severity="info"
                    icon={<Icon>schedule</Icon>}
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    <MDTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Same-day early checkout (informational)
                    </MDTypography>
                    <MDTypography variant="body2" component="span">
                      The guest chose early checkout on the same check-out date. On the checkout
                      date they will check out at the time below (not 12:00 PM). No fee applies.
                      {selectedReservation.earlyCheckout?.requestedCheckOutTime && (
                        <>
                          {" "}
                          Checkout time on checkout date:{" "}
                          {formatDateTime(
                            selectedReservation.earlyCheckout.requestedCheckOutTime
                          )}.{" "}
                        </>
                      )}
                      Check-out date:{" "}
                      {selectedReservation.earlyCheckout?.originalCheckOutDate
                        ? formatDate(selectedReservation.earlyCheckout.originalCheckOutDate)
                        : selectedReservation.checkOutDate
                        ? formatDate(selectedReservation.checkOutDate)
                        : "N/A"}
                      .
                    </MDTypography>
                  </Alert>
                )}
              {selectedReservation.earlyCheckout?.type === "byDays" &&
                selectedReservation.earlyCheckout?.status === "completed" && (
                  <Alert
                    severity="success"
                    icon={<Icon>check_circle</Icon>}
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    <MDTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Early checkout by days
                    </MDTypography>
                    <MDTypography variant="body2" component="span">
                      Original check-out:{" "}
                      {selectedReservation.earlyCheckout?.originalCheckOutDate
                        ? formatDate(selectedReservation.earlyCheckout.originalCheckOutDate)
                        : "N/A"}
                      . New check-out:{" "}
                      {selectedReservation.checkOutDate
                        ? formatDate(selectedReservation.checkOutDate)
                        : "N/A"}
                      . Fee applied (from cancelled days):{" "}
                      {selectedReservation.earlyCheckout?.feeAmount ?? 0} EGP. Refund:{" "}
                      {selectedReservation.earlyCheckout?.refundAmount ?? 0} EGP (to be processed
                      within configured timeframe).
                    </MDTypography>
                  </Alert>
                )}

              {/* Reservation Info */}
              <Card sx={{ p: 2, mb: 3, bgcolor: "grey.50", borderRadius: 2 }}>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Reservation Number
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold">
                    {selectedReservation.reservationNumber || "N/A"}
                  </MDTypography>
                </MDBox>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="medium"
                      display="block"
                      mb={0.5}
                    >
                      Property
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {selectedReservation.property?.title?.en ||
                        selectedReservation.property?.title ||
                        "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="medium"
                      display="block"
                      mb={0.5}
                    >
                      Client
                    </MDTypography>
                    <MDTypography variant="body2">
                      {selectedReservation.client?.fullName ||
                        selectedReservation.client?.email ||
                        "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="medium"
                      display="block"
                      mb={0.5}
                    >
                      Check-In Date
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {selectedReservation.checkInDate
                        ? formatDate(selectedReservation.checkInDate)
                        : "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="medium"
                      display="block"
                      mb={0.5}
                    >
                      Check-Out Date
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      fontWeight="medium"
                      color={actionType === "checkout" ? "warning.main" : "text.primary"}
                    >
                      {selectedReservation.checkOutDate
                        ? formatDate(selectedReservation.checkOutDate)
                        : "N/A"}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.25 }}
                    >
                      Checkout time: {getCheckoutTimeDisplay(selectedReservation)}
                    </MDTypography>
                    {actionType === "checkout" && selectedReservation.checkOutDate && (
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {(() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const checkOutDate = new Date(selectedReservation.checkOutDate);
                          checkOutDate.setHours(0, 0, 0, 0);
                          if (checkOutDate < today) {
                            return "⚠️ Overdue";
                          } else if (checkOutDate.getTime() === today.getTime()) {
                            return "⚠️ Due today";
                          }
                          return "";
                        })()}
                      </MDTypography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="medium"
                      display="block"
                      mb={0.5}
                    >
                      Number of Guests
                    </MDTypography>
                    <MDTypography variant="body2">
                      {selectedReservation.numberOfGuests || 0}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="medium"
                      display="block"
                      mb={0.5}
                    >
                      Number of Nights
                    </MDTypography>
                    <MDTypography variant="body2">
                      {selectedReservation.pricing?.numberOfNights ||
                        (selectedReservation.checkInDate && selectedReservation.checkOutDate
                          ? Math.ceil(
                              (new Date(selectedReservation.checkOutDate) -
                                new Date(selectedReservation.checkInDate)) /
                                (1000 * 60 * 60 * 24)
                            )
                          : "N/A")}
                    </MDTypography>
                  </Grid>
                </Grid>
              </Card>

              {/* Guest Information (Check-In only) */}
              {actionType === "checkin" && selectedReservation && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Icon sx={{ color: "info.main" }}>people</Icon>
                    Guest Information
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" mb={2}>
                    Please provide name for each guest. ID photos (front and back) are optional.
                  </MDTypography>

                  {actionData.guests?.map((guest, index) => (
                    <Card key={index} sx={{ p: 2, mb: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={2}
                        sx={{ color: "info.main" }}
                      >
                        Guest {index + 1}
                      </MDTypography>

                      <Grid container spacing={2}>
                        {/* Guest Name */}
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Guest Name"
                            placeholder="Enter guest full name"
                            value={guest.name || ""}
                            onChange={(e) => {
                              const updatedGuests = [...actionData.guests];
                              updatedGuests[index] = {
                                ...updatedGuests[index],
                                name: e.target.value,
                              };
                              setActionData({ ...actionData, guests: updatedGuests });
                            }}
                            disabled={submitting}
                            required
                          />
                        </Grid>

                        {/* ID Front Photo (optional) */}
                        <Grid item xs={12} sm={6}>
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              display="block"
                              mb={1}
                            >
                              ID Front Photo (optional)
                            </MDTypography>
                            <input
                              accept="image/*"
                              style={{ display: "none" }}
                              id={`id-front-${index}`}
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const updatedGuests = [...actionData.guests];
                                  updatedGuests[index] = {
                                    ...updatedGuests[index],
                                    idFrontPhoto: file,
                                  };
                                  setActionData({ ...actionData, guests: updatedGuests });
                                }
                              }}
                              disabled={submitting}
                            />
                            <label htmlFor={`id-front-${index}`}>
                              <MDButton
                                variant={guest.idFrontPhoto ? "outlined" : "contained"}
                                color={guest.idFrontPhoto ? "success" : "info"}
                                component="span"
                                fullWidth
                                startIcon={<Icon>camera_alt</Icon>}
                                disabled={submitting}
                                sx={{ mb: 1 }}
                              >
                                {guest.idFrontPhoto ? guest.idFrontPhoto.name : "Upload ID Front"}
                              </MDButton>
                            </label>
                            {guest.idFrontPhoto && (
                              <MDTypography
                                variant="caption"
                                color="success.main"
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                              >
                                <Icon sx={{ fontSize: "0.875rem" }}>check_circle</Icon>
                                {guest.idFrontPhoto.name}
                              </MDTypography>
                            )}
                          </MDBox>
                        </Grid>

                        {/* ID Back Photo (optional) */}
                        <Grid item xs={12} sm={6}>
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              display="block"
                              mb={1}
                            >
                              ID Back Photo (optional)
                            </MDTypography>
                            <input
                              accept="image/*"
                              style={{ display: "none" }}
                              id={`id-back-${index}`}
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const updatedGuests = [...actionData.guests];
                                  updatedGuests[index] = {
                                    ...updatedGuests[index],
                                    idBackPhoto: file,
                                  };
                                  setActionData({ ...actionData, guests: updatedGuests });
                                }
                              }}
                              disabled={submitting}
                            />
                            <label htmlFor={`id-back-${index}`}>
                              <MDButton
                                variant={guest.idBackPhoto ? "outlined" : "contained"}
                                color={guest.idBackPhoto ? "success" : "info"}
                                component="span"
                                fullWidth
                                startIcon={<Icon>camera_alt</Icon>}
                                disabled={submitting}
                                sx={{ mb: 1 }}
                              >
                                {guest.idBackPhoto ? guest.idBackPhoto.name : "Upload ID Back"}
                              </MDButton>
                            </label>
                            {guest.idBackPhoto && (
                              <MDTypography
                                variant="caption"
                                color="success.main"
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                              >
                                <Icon sx={{ fontSize: "0.875rem" }}>check_circle</Icon>
                                {guest.idBackPhoto.name}
                              </MDTypography>
                            )}
                          </MDBox>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </MDBox>
              )}

              {/* Criteria Checklist - Confirm items exist before client (check-in) or after (check-out) */}
              {actionData.criteriaConfirmed?.length > 0 && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Icon sx={{ color: "info.main" }}>checklist</Icon>
                    {actionType === "checkin"
                      ? "Confirm items exist (before client checks in)"
                      : "Confirm items exist or mark missing"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" mb={2}>
                    {actionType === "checkin"
                      ? "Verify each item is present. For quantifiable items (e.g. blankets, spoons), confirm the quantity."
                      : "Uncheck items that are missing. Missing items are deducted from insurance."}
                  </MDTypography>
                  <Card sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      {actionData.criteriaConfirmed.map((criterion, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            {!criterion.quantifiable && (
                              <MDBox>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={!!criterion.present}
                                      onChange={(e) => {
                                        const updated = [...actionData.criteriaConfirmed];
                                        updated[idx] = {
                                          ...updated[idx],
                                          present: e.target.checked,
                                          absentType: e.target.checked ? undefined : "missing",
                                        };
                                        setActionData({
                                          ...actionData,
                                          criteriaConfirmed: updated,
                                        });
                                      }}
                                      color={
                                        actionType === "checkout" && !criterion.present
                                          ? "error"
                                          : "primary"
                                      }
                                    />
                                  }
                                  label={
                                    <MDTypography variant="body2">
                                      {criterion.criterionName ||
                                        criterion.criterionNameAr ||
                                        `Item ${idx + 1}`}
                                      {actionType === "checkout" && !criterion.present && (
                                        <Chip
                                          label={
                                            criterion.absentType === "damaged"
                                              ? "Damaged"
                                              : "Missing"
                                          }
                                          size="small"
                                          color={
                                            criterion.absentType === "damaged" ? "warning" : "error"
                                          }
                                          sx={{ ml: 1 }}
                                        />
                                      )}
                                    </MDTypography>
                                  }
                                />
                              </MDBox>
                            )}
                            {criterion.quantifiable && (
                              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <MDTypography variant="body2" sx={{ minWidth: 120 }}>
                                  {criterion.criterionName ||
                                    criterion.criterionNameAr ||
                                    `Item ${idx + 1}`}
                                  :
                                </MDTypography>
                                <TextField
                                  type="number"
                                  size="small"
                                  label={actionType === "checkin" ? "Qty present" : "Qty remaining"}
                                  value={criterion.quantity ?? 1}
                                  onChange={(e) => {
                                    const qty = Math.max(0, parseInt(e.target.value, 10) || 0);
                                    const updated = [...actionData.criteriaConfirmed];
                                    updated[idx] = {
                                      ...updated[idx],
                                      quantity: qty,
                                      present: qty > 0,
                                      absentType:
                                        qty === 0
                                          ? updated[idx]?.absentType || "missing"
                                          : undefined,
                                    };
                                    setActionData({ ...actionData, criteriaConfirmed: updated });
                                  }}
                                  inputProps={{ min: 0 }}
                                  sx={{ width: 100 }}
                                />
                              </MDBox>
                            )}
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>
                  </Card>
                </MDBox>
              )}

              {/* Notes */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                placeholder={`Enter notes for ${
                  actionType === "checkin" ? "check-in" : "check-out"
                }...`}
                value={actionData.notes}
                onChange={(e) => setActionData({ ...actionData, notes: e.target.value })}
                disabled={submitting}
                sx={{ mb: 3 }}
              />

              {/* Damaged Items (Check-Out only) - cost estimate sent in 2-4 days */}
              {actionType === "checkout" && (
                <MDBox mb={3}>
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={1} color="warning.main">
                    Damaged Items (cost estimate will be sent to client in 2-4 days)
                  </MDTypography>
                  <MDBox display="flex" gap={1} alignItems="flex-start" flexWrap="wrap" mb={1}>
                    <TextField
                      size="small"
                      label="Item name"
                      placeholder="e.g. TV, Sofa"
                      value={damagedItemName}
                      onChange={(e) => setDamagedItemName(e.target.value)}
                      disabled={submitting}
                      sx={{ minWidth: 150, flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Description (optional)"
                      placeholder="Brief damage description"
                      value={damagedItemDesc}
                      onChange={(e) => setDamagedItemDesc(e.target.value)}
                      disabled={submitting}
                      sx={{ minWidth: 180, flex: 1 }}
                    />
                    <MDButton
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={handleAddDamagedItem}
                      disabled={submitting || !damagedItemName?.trim()}
                      startIcon={<Icon>add</Icon>}
                    >
                      Add
                    </MDButton>
                  </MDBox>
                  {actionData.damagedItems?.length > 0 && (
                    <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                      {actionData.damagedItems.map((item, idx) => (
                        <Chip
                          key={idx}
                          label={
                            item.description ? `${item.name} (${item.description})` : item.name
                          }
                          color="warning"
                          size="small"
                          onDelete={() => handleRemoveDamagedItem(idx)}
                          sx={{ maxWidth: 280 }}
                        />
                      ))}
                    </MDBox>
                  )}
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() => !submitting && setDialogOpen(false)}
            disabled={submitting}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color={actionType === "checkin" ? "success" : "warning"}
            onClick={handleSubmitAction}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} />
              ) : (
                <Icon>{actionType === "checkin" ? "check" : "check"}</Icon>
              )
            }
            sx={{
              borderRadius: 2,
              boxShadow: `0 4px 12px rgba(${
                actionType === "checkin" ? "76, 175, 80" : "237, 108, 2"
              }, 0.3)`,
            }}
          >
            {submitting
              ? actionType === "checkin"
                ? "Checking In..."
                : "Checking Out..."
              : actionType === "checkin"
              ? "Complete Check-In"
              : "Complete Check-Out"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default CheckInOutDashboard;
